import React, { useState } from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { Link as RouterLink } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import Link from '@material-ui/core/Link';
import FormControl from '@material-ui/core/FormControl';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import FormHelperText from '@material-ui/core/FormHelperText';
import { useWelcomeMessage } from '../../hook/use-welcome-message';
import visibility from '../../assets/icons/visibility.svg';
import visibilityOff from '../../assets/icons/visibilityOff.svg';
import { RecoveryLinkPassword, SubmitButtonContainer } from '../SignUpForm/SignUpForm.style';
import { logIn } from '../../modules/auth';
import { emailRegex, routes } from '../../constants.js/misc';
import { history } from '../../store';
import { AuthFormButton, AuthFormInput, AuthFormLabel } from '../AuthForm/AuthForm.style';
import { Checkbox } from '@material-ui/core';
import { CLARITY_MASK } from '../../utils/clarity';

/**
 * Log in form component
 * @param {function} logIn log in function from the store
 * @param {boolean} isLoggingIn log in loader variable from the store
 * @param {function} handleLogin function to execute on login success
 */
const SignInForm = ({ logIn, isLoggingIn, onLogin }) => {
    const { t } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();
    const { initWelcomeMessage } = useWelcomeMessage();

    const fieldsSchema = yup.object({
        email: yup.string().required('Se requiere un correo electrónico'),
        password: yup.string().required('Se requiere una contraseña'),
    });

    const {
        handleSubmit,
        control,
        register,
        formState: { errors },
    } = useForm({
        resolver: yupResolver(fieldsSchema),
        mode: 'onChange',
    });

    const handleLoginError = () =>
        enqueueSnackbar(t('LogIn.error'), {
            variant: 'error',
            autoHideDuration: 2500,
        });

    const onSubmit = (data) => logIn(data.email, data.password, handleOnLogin, handleLoginError);

    const [password, setPassword] = useState(false);

    const showPassword = () => {
        setPassword((prevState) => !prevState);
    };

    const handleOnLogin = () => {
        initWelcomeMessage();
        history.replace(routes.homeUrl);
        onLogin();
    };

    return (
        <>
            <form
                onSubmit={handleSubmit(onSubmit)}
                style={{ display: 'flex', flexDirection: 'column' }}
            >
                <FormControl variant="filled" margin="normal" fullWidth>
                    <AuthFormLabel error={errors?.email ? true : false}>
                        {t('LogIn.email')}
                    </AuthFormLabel>
                    <Controller
                        name="email"
                        control={control}
                        render={({ field }) => (
                            <AuthFormInput
                                {...field}
                                {...register('email')}
                                error={errors?.email ? true : false}
                                fullWidth
                                placeholder={t('LogIn.emailPlaceholder')}
                                type="email"
                            />
                        )}
                        rules={{ required: true, pattern: emailRegex }}
                    />
                    <FormHelperText error={errors?.email ? true : false}>
                        {errors?.email ? t('LogIn.emailError') : ''}
                    </FormHelperText>
                </FormControl>
                <FormControl variant="filled" margin="normal" fullWidth {...CLARITY_MASK}>
                    <AuthFormLabel error={errors?.password ? true : false}>
                        {t('LogIn.password')}
                    </AuthFormLabel>
                    <Controller
                        name="password"
                        control={control}
                        render={({ field }) => (
                            <AuthFormInput
                                {...field}
                                {...register('password')}
                                error={errors?.password ? true : false}
                                fullWidth
                                placeholder={t('LogIn.passwordPlaceholder')}
                                type={password ? 'text' : 'password'}
                            />
                        )}
                        rules={{ required: true }}
                    />
                    <img
                        style={{
                            width: '25px',
                            height: '25px',
                            position: 'absolute',
                            right: '15px',
                            top: '33px',
                            cursor: 'pointer',
                        }}
                        src={password ? visibilityOff : visibility}
                        onClick={showPassword}
                    />
                    <FormHelperText error={errors?.password ? true : false}>
                        {errors?.password ? t('LogIn.passwordError') : ''}
                    </FormHelperText>
                </FormControl>
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                    }}
                >
                    <FormControlLabel
                        control={<Checkbox color="primary" name="checkedC" />}
                        label={t('LogIn.rememberMe')}
                        style={{ color: 'white' }}
                    />
                    <RecoveryLinkPassword>
                        <Link variant="secondary" component={RouterLink} to="/password-recovery">
                            {t('RecoveryPassWord.link')}
                        </Link>
                    </RecoveryLinkPassword>
                </div>
                <div>
                    <AuthFormButton
                        disabled={isLoggingIn}
                        type="submit"
                        size="medium"
                        variant="contained"
                        fullWidth
                    >
                        {t('LogIn')}
                    </AuthFormButton>
                </div>
            </form>
        </>
    );
};

const mapStateToProps = ({ auth }) => ({
    isLoggingIn: auth.isLoggingIn,
});

const mapDispatchToProps = (dispatch) => bindActionCreators({ logIn }, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(SignInForm);
